import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { NotificationsService } from './notifications.service';
import { AuthStateService } from './authstate.service';
import { Router } from '@angular/router';
import { AuthData } from '../interfaces/generic.interface';
import { WebsocketService } from './websocket.service';

export type AuthStates = "INIT" | "NOT_LOGGED" | "LOGGED" | "ERROR";

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService implements OnDestroy {
  destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private notifications: NotificationsService,
    private authState: AuthStateService,
    private router: Router,
    private ws: WebsocketService,
    @Inject('ENVIRONMENT') private ENV: any
  ) {
    let authRaw = localStorage.getItem('auth');
    if (authRaw !== null) {
      let auth = JSON.parse(authRaw);
      if (auth.expiration <= Date.now()) {
        localStorage.removeItem('auth');
        this.authState.logout(true);
      } else {
        this.performAutoLogin(auth.token)
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  performLogin(loginData: AuthData): void {
    this.http.post(this.ENV.apiUrl + "login", loginData).subscribe({
      next: (resp: any) => {
        this.notifications.showNotification({
          severity: "success",
          title: "loginSuccess",
          detail: "welcomeBack",
          detailParams: {
            username: resp.data.user.username,
            gender: resp.data.user.gender
          },
          translateDetailsParams: false,
          life: 1500,
          addToHistory: false
        })
        this.loginCallback(resp.data)
      },
      error: () => {
        this.authState.changes$.next("ERROR")
      }
    })
  }

  performAutoLogin(token: string): void {
    this.authState.changes$.next("AUTOLOGIN_EXECUTION")
    this.http.post(this.ENV.apiUrl + "autoLogin", {
      token: token
    }).subscribe({
      next: (resp: any) => {
        this.loginCallback(resp.data)
      },
      error: () => {
        this.authState.changes$.next("ERROR")
        localStorage.removeItem('auth');
        sessionStorage.removeItem('returnUrl');
      }
    })
  }

  loginCallback(resp: any) {
    let expiration = new Date(resp.expiration);
    let diff = expiration.getTime() - new Date().getTime();
    setTimeout(() => {
      this.notifications.showNotification({
        severity: "warn",
        title: "sessionExpiring",
        detail: "sessionExpiringMinutes",
        detailParams: {
          minutes: Math.floor(diff / 1000 / 60)
        },
        translateDetailsParams: false,
        addToHistory: false,
        life: 5000
      })
    }, (diff - 60 * 1000));

    setTimeout(() => {
      this.logout(true, this.router.url)
      this.notifications.showNotification({
        severity: "warn",
        title: "errors.loginExpired",
        addToHistory: false,
      })
    }, diff);

    this.ws.connectWs(resp.token)
    this.authState.loginData = resp;
    this.authState.token = resp.token;
    this.authState.changes$.next("LOGGED");
    localStorage.setItem('auth', JSON.stringify({
      token: resp.token,
      expiration: expiration.getTime()
    }));
    if (Boolean(this.authState.loginData?.user?.emailVerified ?? 0)) {
      this.router.navigate(sessionStorage.getItem('returnUrl')?.split('/') ?? ['/home']);
    } else {
      this.router.navigate(['/emailVerification']);
    }
  }

  performSignup(signupData: AuthData): void {
    this.http.post(this.ENV.apiUrl + "signup", signupData).subscribe({
      next: (resp: any) => {
        this.notifications.showNotification({
          severity: "success",
          title: "signupSuccess",
          detail: "verifyEmailToProceed",
          addToHistory: false
        })
        this.loginCallback(resp.data)
      },
      error: () => {
        this.authState.changes$.next("ERROR")
      }
    })
  }

  requestNewVerificationEmail(): void {
    if (!this.authState.token) {
      this.authState.logout(true);
      return;
    }

    this.http.post(this.ENV.apiUrl + "resendVerificationEmail", {
      token: this.authState.token
    }).subscribe({
      next: (resp: any) => {
        this.notifications.showNotification({
          severity: "success",
          title: "verificationEmailSent",
          detail: "checkYourEmail",
          addToHistory: false
        })
      },
      error: () => {

      }
    })
  }

  verifyEmail(otp: string): void {
    if (!this.authState.token) {
      this.authState.logout(true);
      return;
    }

    this.http.post(this.ENV.apiUrl + "verifyEmail", {
      token: this.authState.token,
      code: otp
    }).subscribe({
      next: (resp: any) => {
        this.notifications.showNotification({
          severity: "success",
          title: "accountVerified",
          addToHistory: false
        })
        this.router.navigate(['/home']);
      },
      error: () => {
      }
    })
  }

  getCurrentUser() {
    return this.authState.loginData.user;
  }

  logout(redirect: boolean = true, url?: string): void {
    this.authState.logout(redirect, url);
  }
}
